import React from 'react';
import { LabeledSectionSelectBox } from './LabeledSectionSelectBox';
import { groupBy, getRatioAsPercentage } from './utils';
import { MonthlySectionDataChart } from './MonthlySectionDataChart';
import { MonthlySectionDataTable } from './MonthlySectionDataTable';
import { LabeledProductTypeSelectBox } from './LabeledProductTypeSelectBox';
import { ALL_PRODUCT_TYPES, ALL_PRODUCT_TYPES_STACKED } from './constants';
import { MonthlySectionDataChartStacked } from './MonthlySectionDataChartStacked';
import './styles.css';
import { LabeledSelectBox } from './LabeledSelectBox';
import { CollapsibleTableContainer } from './CollapsibleTableContainer';

export const ProductDecisions = ({
    rawData,
    isLoading,
    yPropertyName,
    yAxisLabel,
    sectionNumber,
    sections,
    handleSectionChanged,
    productTypeName,
    handleProductTypeChanged,
    startYear,
    handleStartYearChanged,
}) => {
    if (isLoading) {
        return <p>Loading manufacturer data...</p>
    }

    if (!rawData) {
        return <p>Please select a manufacturer.</p>;
    }

    // To support product-specific graphs, the service returns data per product, not per section.
    // But the table as well as the "All Product Types" version of the chart 
    //  need the data for ALL product types each month, for each section.
    const decisionsTotalled = getTotalledProductDecisions(rawData);

    // Filter chart on selected section. Use totalled or product-separate data depending on PT filter.
    let selectedSectionData;
    if (!sectionNumber) {
        selectedSectionData = null;
    } else if (!productTypeName || productTypeName === ALL_PRODUCT_TYPES) {
        selectedSectionData = decisionsTotalled.filter(d => d.sectionNumber === sectionNumber)
    } else {
        selectedSectionData = rawData.filter(d => d.sectionNumber === sectionNumber)
    }

    // Get product types in selected section for product type selection list.
    let productTypes = rawData && sectionNumber
        ? Array.from(
            new Set(rawData
                .filter(d => d.sectionNumber === sectionNumber)
                .map(d => d.productTypeName)))
            .sort()
        : null;

    // If a specific product type is selected, filter chart on it.
    if (selectedSectionData
        && productTypeName
        && productTypeName !== ALL_PRODUCT_TYPES
        && productTypeName !== ALL_PRODUCT_TYPES_STACKED) {
        selectedSectionData = selectedSectionData.filter(d => d.productTypeName === productTypeName);
    }

    const years = selectedSectionData
        ? Array.from(
            new Set(selectedSectionData.map(d => parseInt(d.year))))
            .sort()
        : [];
    const startYearInt = parseInt(startYear);
    let tableData = decisionsTotalled;
    if (startYearInt && selectedSectionData && years.includes(startYearInt)) {
        selectedSectionData = selectedSectionData.filter(d => d.year >= startYearInt);
        tableData = tableData.filter(d => d.year >= startYearInt);
    }

    var chart = productTypeName === ALL_PRODUCT_TYPES_STACKED
        ? <MonthlySectionDataChartStacked rawData={selectedSectionData} yPropertyName={yPropertyName} yAxisLabel={yAxisLabel} />
        : <MonthlySectionDataChart rawData={selectedSectionData} yPropertyName={yPropertyName} yAxisLabel={yAxisLabel} />;

    return (
        <div>
            <LabeledSectionSelectBox selectedSectionNumber={sectionNumber} sections={sections} onSectionChanged={handleSectionChanged} />
            <LabeledProductTypeSelectBox selectedProductTypeName={productTypeName} productTypeNames={productTypes} onProductTypeChanged={handleProductTypeChanged} />
            <LabeledSelectBox
                label="Starting Year"
                items={years}
                selectedValue={startYearInt}
                onSelectionChanged={handleStartYearChanged} />
            {chart}
            <CollapsibleTableContainer>
                <MonthlySectionDataTable rawData={tableData} dataMember={yPropertyName} />
            </CollapsibleTableContainer>
        </div >
    );
}

function getTotalledProductDecisions(rawData) {
    // Sum up total decisions and won decisions across all product types for each section.
    const productDecisions = rawData.map(d => {
        return {
            total: d.total,
            won: d.won,
            month: d.month,
            sectionNumber: d.sectionNumber,
            sectionTitle: d.sectionTitle,
            year: d.year,
            productTypeName: d.productTypeName,
            key: `${d.sectionNumber}:${d.month}-${d.year}`
        };
    });
    const decisionListsBySectionAndMonth = groupBy(productDecisions, "key");
    const decisionsBySectionAndMonth = new Map();
    Object.values(decisionListsBySectionAndMonth)
        .forEach(decisionsForSectionAndMonth => {
            for (const decisionsForProduct of decisionsForSectionAndMonth) {
                let decisionsForMonth = {};
                const key = decisionsForProduct.key;
                if (decisionsBySectionAndMonth.has(key)) {
                    decisionsForMonth = decisionsBySectionAndMonth.get(key);
                    decisionsForMonth.total += decisionsForProduct.total;
                    decisionsForMonth.won += decisionsForProduct.won;
                }
                else {
                    decisionsForMonth = Object.assign(decisionsForMonth, decisionsForProduct);
                    decisionsBySectionAndMonth.set(key, decisionsForMonth);
                }
            }
        });

    // Compute overall win % for each section.
    const productDecisionsAggregated = Array.from(decisionsBySectionAndMonth.values())
        .map(d => {
            return {
                total: d.total,
                won: d.won,
                winPercentage: getRatioAsPercentage(d.won, d.total),
                month: d.month,
                sectionNumber: d.sectionNumber,
                sectionTitle: d.sectionTitle,
                year: d.year,
                productTypeName: d.productTypeName
            }
        });

    return productDecisionsAggregated;
}